import { Button } from '@components/Buttons/Base'
import { Spacer } from '@components/Spacer'
import { useDesignatedDonation } from '@features/checkout/hooks/useDesignatedDonation'
import { LocationTag } from '@features/needs/components/LocationTag'
import { useOrgActions } from '@features/organizations/hooks/useUserOrgs'
import { CustomSpacer } from '@features/teams/components/CustomSpacer'
import { cdnUrlFromPath } from '@lib/utils/cdnUrlFromPath'
import * as Colors from '@themes/Colors'
import Fonts from '@themes/Fonts'
import { openBrowserAsync } from 'expo-web-browser'
import * as React from 'react'
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import { useLink } from 'solito/link'
import { CircleImage } from '~/Components/CircleImage/Original'
import { useOrg } from '../hooks/useOrganizations'
import { OrgActionMenu as ActionMenu } from './ActionMenu'
import { FollowersTag } from './FollowersTag'

export function OrgHeader(props: { aliasOrValias: string }) {
  const query = useOrg(props.aliasOrValias)
  const { isFollower, follow, unfollow } = useOrgActions({
    aliasOrValias: props.aliasOrValias,
  })

  const designatedLinkProps = useLink({
    href: '/organization/[alias]/give',
    as: `/organization/${props.aliasOrValias}/give`,
  })

  const websiteLinkProps = useLink({
    href: query.data?.url || '',
  })

  const { enabled } = useDesignatedDonation({
    type: 'org',
    aliasOrValias: props.aliasOrValias,
  })

  if (query.isLoading || query.isLoadingError)
    return (
      <View style={styles.container}>
        <ActivityIndicator />
      </View>
    )

  if (query.data != null) {
    const orgProfilePic = query.data.profile_image_path
      ? cdnUrlFromPath(query.data.profile_image_path)
      : undefined

    return (
      <View role="banner" style={styles.container}>
        <CustomSpacer height={16} />

        <View style={styles.headerRow}>
          <View>
            <CircleImage
              image={orgProfilePic}
              size={80}
              borderWidth={1}
              fallbackToGradient
              hideShadow
            />
          </View>
        </View>

        <CustomSpacer height={16} />

        <View style={styles.headerRow}>
          <View style={styles.orgNameHolder}>
            <Text style={styles.orgName}>{query.data.name}</Text>
          </View>
        </View>

        <CustomSpacer height={18} />

        <View style={styles.headerRow}>
          <Text style={styles.description}>{query.data.description}</Text>
        </View>

        <CustomSpacer height={18} />

        <View style={styles.headerRow}>
          <View
            style={{
              flexGrow: 1,
              flexShrink: 0,
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            {query.data.location ? (
              <LocationTag
                city={query.data.location.city || undefined}
                state={query.data.location.state || undefined}
              />
            ) : null}

            {query.data.stats?.follower_count ? (
              <FollowersTag count={query.data.stats.follower_count} />
            ) : null}
          </View>
        </View>

        <CustomSpacer height={18} />

        <View style={styles.headerRow}>
          {isFollower ? (
            <Button
              format="rounded"
              emphasis="primary"
              fullFlex
              size="small"
              title="Following"
              onClick={unfollow}
            />
          ) : (
            <Button
              format="rounded"
              emphasis="primary"
              fullFlex
              size="small"
              title="Follow"
              onClick={follow}
            />
          )}

          <Spacer width={8} noShrink />

          {enabled && Platform.OS === 'web' ? (
            <Button
              as="a"
              format="rounded"
              emphasis="purple"
              fullFlex
              size="small"
              title="Give More 🎉"
              {...designatedLinkProps}
              onClick={() => designatedLinkProps.onPress}
            />
          ) : (
            <Button
              as="a"
              format="rounded"
              emphasis="primary"
              fullFlex
              size="small"
              title="Website"
              {...websiteLinkProps}
              onClick={() => {
                if (Platform.OS === 'web') {
                  websiteLinkProps.onPress()
                } else {
                  openBrowserAsync(query.data?.url || '', {
                    toolbarColor: Colors.white,
                    enableBarCollapsing: true,
                    // iOS Properties
                    dismissButtonStyle: 'close',
                    readerMode: false,
                    // Android Properties
                    showTitle: true,
                    secondaryToolbarColor: Colors.realBlack,
                    enableDefaultShareMenuItem: true,
                    // Web Properties
                    windowName: '_blank',
                  })
                }
              }}
            />
          )}
        </View>

        <CustomSpacer height={12} />

        <View style={styles.sectionSeparator} />
      </View>
    )
  }

  return null
}

const styles = StyleSheet.create({
  container: {
    minHeight: 250,
    // flexBasis: 275,
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'center',
    alignContent: 'center',
    // minHeight: 250,
    // {
    // flex: 1,
    // flexShrink: 0,
    flexDirection: 'column',
    // flexBasis: '100%',
    backgroundColor: Colors.white,
  },

  orgNameHolder: {
    flexGrow: 1,
    width: '100%',
  },
  orgName: {
    alignSelf: 'flex-start',
    color: Colors.alternateGray,
    fontFamily: Fonts.type.bold,
    fontWeight: 'bold',
    fontSize: 22,
    lineHeight: 22,
    marginTop: 8,
  },

  description: {
    color: '#646670',
    fontFamily: Fonts.type.regular,
    fontSize: 16,
    lineHeight: 22,
  },
  sectionSeparator: {
    height: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'stretch',
    flexGrow: 0,
    backgroundColor: Colors.altWhite,
    paddingHorizontal: 16,
  },

  headerRow: {
    flexDirection: 'row',
    overflow: 'hidden',
    paddingHorizontal: 16,
  },
})
